import { Component } from '@angular/core'
import { DialogRef } from '@angular/cdk/dialog'
import { selectImageLayers, selectSegmentationLayers } from 'src/app/state/viewer/viewer.selectors'
import { Store } from '@ngrx/store'
import { StaticLayerConfig } from 'src/app/state/viewer/viewer.state'
import { selectCaseSegObjectPropertyMap } from 'src/app/state/case/case.selectors'
import { PipelineType } from 'src/app/shared/model/jobs'
import { HttpErrorResponse } from '@angular/common/http'
import { closeJobSelectionDialog } from 'src/app/shared/utils/job-selection-dialog-utils'
import { take } from 'rxjs'
import { ToasterService } from 'src/app/shared/services/toaster.service'
import { JobApiService } from 'src/app/shared/services/job-api.service'

interface TreeNode {
    id: string
    show: boolean
    selected: boolean
    isTopLevel?: boolean
    nodes: TreeNode[]
}

@Component({
    selector: 'app-add-properties-dialog',
    templateUrl: './add-properties-dialog.component.html',
    styleUrls: ['./add-properties-dialog.component.scss'],
})
export class AddPropertiesDialogComponent {
    readonly caseSegObjectPropertyMap = this.store.select(selectCaseSegObjectPropertyMap)
    readonly segmentationLayers$ = this.store.select(selectSegmentationLayers)
    readonly markerLayers$ = this.store.select(selectImageLayers)
    baseLayer: StaticLayerConfig | null = null
    selectedLayer1: StaticLayerConfig | null = null
    selectedLayer2: StaticLayerConfig | null = null
    radius: number = 1
    summary: string = 'mean'

    nodes: TreeNode[] = [
        {
            id: 'Segmentation',
            show: false,
            selected: false,
            isTopLevel: true,
            nodes: [],
        },
        {
            id: 'Markers',
            show: false,
            selected: false,
            isTopLevel: true,
            nodes: [],
        },
    ]

    constructor(
        readonly store: Store,
        private readonly dialogRef: DialogRef<string>,
        private readonly jobApi: JobApiService,
        private readonly toaster: ToasterService
    ) {
        this.caseSegObjectPropertyMap.subscribe(caseSegObjectPropertyMap => {
            this.segmentationLayers$.subscribe(segmentationLayers => {
                this.baseLayer =
                    segmentationLayers.find(
                        layer => layer.source === caseSegObjectPropertyMap?.segSource
                    ) ?? null

                const segmentationNode = this.nodes.find(node => node.id === 'Segmentation')
                if (segmentationNode) {
                    segmentationNode.nodes = segmentationLayers.map(layer => ({
                        id: layer.name!,
                        show: false,
                        selected: false,
                        nodes: [],
                    }))
                }
            })
        })

        this.markerLayers$.subscribe(markerLayers => {
            const markersNode = this.nodes.find(node => node.id === 'Markers')
            if (markersNode) {
                markersNode.nodes = markerLayers.map(layer => ({
                    id: layer.name!,
                    show: false,
                    selected: false,
                    nodes: [],
                }))
            }
        })
    }

    cancel(): void {
        this.dialogRef.close(undefined)
    }

    hasChildren(node: TreeNode): boolean {
        return node.nodes && node.nodes.length > 0
    }

    toggleNode(node: TreeNode) {
        node.show = !node.show
    }

    selectSelection(node: TreeNode) {
        node.selected = !node.selected
        if (node.isTopLevel && node.nodes.length > 0) {
            node.nodes.forEach(child => {
                child.selected = node.selected
            })
        }
    }

    confirm(): void {
        this.caseSegObjectPropertyMap.pipe(take(1)).subscribe(caseSegObjectPropertyMap => {
            for (let i = 0; i < 2; i++) {
                if (this.markerIsSelected(this.nodes[i])) {
                    const jobParams: any = {
                        type: PipelineType.MapIntensity,
                        target: this.baseLayer!.name,
                        source: this.nodes[i].nodes.filter(n => n.selected).map(n => n.id),
                        segObjectPropertyMap: caseSegObjectPropertyMap,
                        mask: undefined,
                        batch: false,
                    }

                    if (i === 1) {
                        jobParams.radius = this.radius
                        jobParams.summary = this.summary
                    }

                    this.jobApi.startIntensityJob(jobParams).subscribe({
                        next: () => closeJobSelectionDialog(),
                        error: (err: HttpErrorResponse) => {
                            this.toaster.httpError(err, 'Error starting job')
                        },
                    })
                }
            }
        })
        this.dialogRef.close()
    }

    markerIsSelected(node: TreeNode): boolean {
        return node.nodes.find(n => n.selected) !== undefined
    }
}
